import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ResellerLicenseTypesService } from '@services/license-types.service';
import { LicenseTypeSummary } from 'mbs-ui-shared@models/License';
import { LicenseType } from 'mbs-ui-shared@models/LicenseType.enum';
import { filter, pipe, switchMap, tap } from 'rxjs';

interface LicenseTypesState {
  allLicenseTypes: LicenseTypeSummary[] | null;
  allLicenseTypesLoading: boolean;
  allLicenseTypesLoaded: boolean;
}

const initialState: LicenseTypesState = {
  allLicenseTypes: null,
  allLicenseTypesLoading: false,
  allLicenseTypesLoaded: false
};

export const LicenseTypesStore = signalStore(
  { providedIn: 'root' },
  withState<LicenseTypesState>(initialState),
  withComputed((store) => ({
    allLicenseTypesMap: computed(
      () => new Map<LicenseType, string>((store.allLicenseTypes() ?? []).map((item) => [item.type, item.displayName]))
    )
  })),
  withMethods((store, service = inject(ResellerLicenseTypesService)) => ({
    loadAllLicenseTypes: rxMethod<boolean>(
      pipe(
        filter((force) => force || (!store.allLicenseTypesLoaded() && !store.allLicenseTypesLoading())),
        tap(() => patchState(store, { allLicenseTypesLoading: true })),
        switchMap(() =>
          service.getTypes().pipe(
            tapResponse({
              next: (types) =>
                patchState(store, {
                  allLicenseTypes: types,
                  allLicenseTypesLoaded: true,
                  allLicenseTypesLoading: false
                }),
              error: () => patchState(store, { allLicenseTypesLoading: false })
            })
          )
        )
      )
    )
  }))
);

export type LicenseTypesStoreType = InstanceType<typeof LicenseTypesStore>;
